import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BehaviorSubject, Observable, Subscription, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import {
  OdooBitacoraService, OdooGrupoDia, OdooMovimiento, OdooMovimientosFiltro, agruparPorDia,
} from 'src/app/services/odoo/odoo-bitacora.service';
import { OdooCarga } from 'src/app/services/odoo/odoo.models';
import { OdooService, conCarga } from 'src/app/services/odoo/odoo.service';

interface VistaBitacora { cargando: boolean; error: string | null; hayDatos: boolean; total: number; conProblema: number; grupos: OdooGrupoDia[]; }

const fHora = new Intl.DateTimeFormat('es-VE', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Caracas' });

const fFecha = new Intl.DateTimeFormat('es-VE', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Caracas' });

/** BITÁCORA — la memoria de lo que pasó con Odoo, en frases y agrupada por día. Se guarda 180 días. */
@Component({
  selector: 'app-odoo-bitacora',
  templateUrl: './odoo-bitacora.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OdooBitacoraComponent implements OnDestroy {
  readonly limite = 20;
  filtro: OdooMovimientosFiltro = { sobre: '', ok: '', q: '', page: 1, limit: this.limite };
  readonly buscar = new FormControl('');
  sel: OdooMovimiento | null = null;
  readonly esperando = [0, 1, 2, 3, 4, 5];
  /** Cuántos clientes/comercios esperan envío (resumen.maestros.pendientes); null si no se pudo leer. */
  readonly pendientes$: Observable<number | null> = this.odoo.resumen().pipe(
    map((r) => (r && r.maestros ? r.maestros.pendientes : null)),
    catchError(() => of(null)),
  );

  readonly sobres: { valor: OdooMovimientosFiltro['sobre']; texto: string }[] = [
    { valor: '', texto: 'Todo' }, { valor: 'cliente', texto: 'Clientes' }, { valor: 'comercio', texto: 'Comercios' },
    { valor: 'archivo', texto: 'Archivos' }, { valor: 'conexion', texto: 'Conexión' },
  ];
  readonly resultados: { valor: OdooMovimientosFiltro['ok']; texto: string }[] = [
    { valor: '', texto: 'Todos' }, { valor: 'true', texto: 'Bien' }, { valor: 'false', texto: 'Con problema' },
  ];

  private readonly params$ = new BehaviorSubject<OdooMovimientosFiltro>(this.filtro);
  private readonly sub: Subscription;
  readonly vm$: Observable<VistaBitacora> = conCarga(this.params$, (f) => this.api.movimientos(f)).pipe(
    map((c: OdooCarga<import('src/app/services/odoo/odoo-bitacora.service').OdooMovimientosPagina>): VistaBitacora => ({
      cargando: c.cargando, error: c.error, hayDatos: !!c.datos,
      total: c.datos ? c.datos.total : 0,
      conProblema: c.datos ? c.datos.con_problema : 0,
      grupos: c.datos ? agruparPorDia(c.datos.items) : [],
    })),
  );

  constructor(private api: OdooBitacoraService, private odoo: OdooService) {
    this.sub = this.buscar.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((q: string | null) => this.cambiar({ q: q || '', page: 1 }));
  }

  ngOnDestroy(): void { this.sub.unsubscribe(); }

  cambiar(parcial: Partial<OdooMovimientosFiltro>): void {
    this.filtro = { ...this.filtro, ...parcial };
    this.cerrarPanel();
    this.params$.next(this.filtro);
  }
  elegirSobre(v: string): void { this.cambiar({ sobre: v as OdooMovimientosFiltro['sobre'], page: 1 }); }
  elegirResultado(v: string): void { this.cambiar({ ok: v as OdooMovimientosFiltro['ok'], page: 1 }); }
  recargar(): void { this.cerrarPanel(); this.params$.next(this.filtro); }
  irPagina(p: number): void { this.cambiar({ page: p }); }

  cerrarPanel(): void { this.sel = null; }

  /** Abre el panel; el detalle (<od-evento-detalle>) pide lo enviado y respondido. */
  abrir(m: OdooMovimiento): void { this.sel = m; }

  fechaHora(iso: string): string { return `${fFecha.format(new Date(iso))} · ${this.hora(iso)}`; }

  hora(iso: string): string { return fHora.format(new Date(iso)); }

  porGrupo = (_: number, g: OdooGrupoDia): string => g.clave;
  porId = (_: number, m: OdooMovimiento): string => m._id;
  porValor = (_: number, o: { valor: string }): string => o.valor;
  porIndice = (i: number): number => i;
}
